import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'

export default function FestivalRetainerPage() {
  const breadcrumbs = generateBreadcrumbs('/services/festival-retainer', breadcrumbLabels)

  useSEO({
    title: 'Festival Digital Growth Retainer | Year-Round Support',
    description:
      'Year-round digital support for classical music festivals. Website updates, email campaigns, analytics, and strategy aligned with your recruitment cycle.',
    url: '/services/festival-retainer',
    canonical: '/services/festival-retainer',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Festival Digital Growth Retainer',
      description:
        'Ongoing digital support for classical music festivals including website updates, email campaigns, and strategic consulting.',
      url: '/services/festival-retainer',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const tiersRef = useStaggeredGrid<HTMLDivElement>('.tier-card', { stagger: 0.12 })
  const includedRef = useScrollReveal<HTMLDivElement>('.included-item', { stagger: 0.08 })
  const timelineRef = useScrollReveal<HTMLDivElement>('.timeline-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Year-Round Digital Support for Your Festival</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your festival doesn&apos;t stop after launch season. You need email campaigns during application season.
          Website updates throughout the year. Analytics to track enrollment. SEO improvements for next year&apos;s
          recruitment.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Our Festival Digital Growth Retainer gives you ongoing support tailored to your festival cycle—so you can
          focus on creating transformative musical experiences while we handle your digital strategy.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          Think of us as an extension of your team—someone who understands both the music and the tech.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Schedule a Strategy Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Festivals Need Year-Round Digital Support</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Seasonal Recruitment Cycles',
              description:
                'Your digital needs change throughout the year. Application season requires email nurturing. Post-season needs website updates and enrollment analysis. You need someone who understands this rhythm.',
            },
            {
              problem: 'Ongoing Website Updates',
              description:
                'Faculty changes. Programs evolve. Schedules shift. You need someone who can update your website quickly without waiting weeks for a developer to respond.',
            },
            {
              problem: 'Email Campaigns During Application Season',
              description:
                "Inquiry nurture emails. Application reminders. Enrollment confirmations. Alumni updates. These don't write and send themselves—you need consistent campaign management.",
            },
            {
              problem: 'No Time for SEO or Analytics',
              description:
                'You know you should be tracking website traffic, application sources, and enrollment metrics. But who has time? You need someone monitoring this continuously.',
            },
            {
              problem: 'Technical Issues & Bug Fixes',
              description:
                "Forms break. Integrations stop working. Emails bounce. When tech issues happen mid-application season, you can't wait days for a fix. You need immediate support.",
            },
            {
              problem: 'No Strategic Partner',
              description:
                "You need someone who understands your festival's goals, tracks progress year over year, and proactively suggests improvements. Not just a vendor who builds once and disappears.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          A one-time website build isn&apos;t enough. Festivals need ongoing digital support aligned with their
          recruitment and operations cycle.
        </p>
      </section>

      {/* Retainer Tiers Section */}
      <section className="mx-auto max-w-5xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">Three Retainer Tiers</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Choose the level of support that matches your festival&apos;s growth stage and digital needs.
          </p>
        </div>

        <div ref={tiersRef} className="grid gap-6 md:grid-cols-3">
          {/* Essential Support */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">Essential Support</h3>
              <p className="text-primary mt-1 text-lg font-bold">$1,500-$2,500/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For festivals that need reliable maintenance and basic updates.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Website hosting & maintenance',
                'Monthly content updates (programs, faculty, schedules)',
                'Technical support & bug fixes',
                'Basic analytics reporting',
                'Email support (48-hour response)',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Growth Partner */}
          <motion.div
            className="tier-card border-primary bg-primary/5 space-y-4 rounded-lg border-2 p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary text-xs font-semibold tracking-wider uppercase">Most Popular</div>
            <div>
              <h3 className="text-xl font-semibold text-white">Growth Partner</h3>
              <p className="text-primary mt-1 text-lg font-bold">$3,000-$4,500/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For festivals focused on growing enrollment and digital presence.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Essential Support',
                'Monthly email campaigns (applicant nurture, alumni)',
                'Application funnel optimization',
                'SEO monitoring & improvements',
                'Quarterly strategy calls',
                'Priority support (24-hour response)',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Full-Service Digital */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">Full-Service Digital</h3>
              <p className="text-primary mt-1 text-lg font-bold">$5,000-$6,000/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For festivals that want a comprehensive digital growth partner.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Growth Partner',
                'CRM management & optimization',
                'Advanced analytics & enrollment tracking',
                'Social media content strategy',
                'Paid ad campaign management (ad spend separate)',
                'Weekly check-ins & on-call support',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What's Included Across All Tiers */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What&apos;s Included Across All Tiers</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center">
          Core services that every festival retainer includes, regardless of tier.
        </p>

        <div ref={includedRef} className="space-y-6">
          {[
            {
              title: 'Festival Cycle Planning',
              description:
                'We align digital strategy with your recruitment timeline—from summer planning to application season to enrollment close.',
            },
            {
              title: 'Ongoing Website Updates',
              description:
                'Program changes, faculty updates, schedule adjustments, and content refreshes handled promptly.',
            },
            {
              title: 'Technical Maintenance',
              description:
                'Hosting, security updates, backups, SSL management, and performance monitoring—all managed for you.',
            },
            {
              title: 'Analytics & Reporting',
              description:
                "Track website traffic, application funnel metrics, and enrollment data to understand what's working.",
            },
            {
              title: 'Strategic Guidance',
              description:
                "Regular consultation on digital strategy, enrollment optimization, and tech improvements tailored to your festival's goals.",
            },
            {
              title: 'Direct Access',
              description:
                'No ticketing systems or endless back-and-forth. Email, call, or Slack directly with someone who knows your festival.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="included-item border-primary bg-background/50 rounded-lg border p-5 backdrop-blur-sm"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">How Your Retainer Works</h2>
        <div ref={timelineRef} className="space-y-6">
          {[
            {
              month: 'January-March: Planning Season',
              description:
                'We help plan your summer recruitment strategy, update website content for the new season, and prepare email campaigns for application launch.',
            },
            {
              month: 'April-May: Pre-Season Push',
              description:
                'Email campaigns to nurture inquiries, SEO improvements to rank for summer program searches, last-minute website updates before applications open.',
            },
            {
              month: 'June-August: Application Season',
              description:
                'Automated email sequences nurturing applicants, funnel optimization to reduce drop-offs, real-time analytics tracking, technical support for any issues.',
            },
            {
              month: 'September-November: Post-Season Review',
              description:
                "Analyze enrollment data, review what worked and what didn't, plan improvements for next year, update website with new faculty/programs.",
            },
            {
              month: 'December: Strategic Planning',
              description:
                'Year-end strategy session, set digital goals for next year, prepare budget for additional projects if needed.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="timeline-item border-primary space-y-2 border-l-4 py-3 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.month}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground pt-4 text-center italic">
          Your retainer adapts to your festival&apos;s seasonal rhythm—not a generic monthly checklist.
        </p>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Festivals Choose Retainer Support</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Predictable Monthly Investment',
              description: 'No surprise invoices. Budgetable monthly cost. Pause anytime if your needs change.',
            },
            {
              benefit: 'Continuous Improvement',
              description:
                'We track metrics year over year and continuously optimize your funnel, website, and campaigns for better enrollment.',
            },
            {
              benefit: 'Faster Response Times',
              description:
                'Retainer clients get priority support. Bug fixes within 24 hours. Content updates within 48 hours. No waiting weeks for availability.',
            },
            {
              benefit: 'Proactive Strategy',
              description:
                "We don't wait for you to ask. We proactively suggest improvements, identify opportunities, and keep you ahead of the curve.",
            },
            {
              benefit: 'Deep Festival Knowledge',
              description:
                'After a few months, we know your programs, your audience, your goals. We become true partners, not just vendors.',
            },
            {
              benefit: 'Enrollment Growth Over Time',
              description:
                'Small optimizations compound. Email campaigns improve. SEO rankings increase. Funnel conversion rates rise. Enrollment grows year over year.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can we start with a lower tier and upgrade later?',
              answer:
                'Absolutely. Many festivals start with Essential Support and upgrade to Growth Partner as their needs grow. You can adjust your tier anytime.',
            },
            {
              question: 'What happens if we need to pause the retainer?',
              answer:
                "No problem. You can pause your retainer anytime (for example, during off-season when you don't need active support). Just give us 30 days notice.",
            },
            {
              question: 'Do we need to have our website built by you first?',
              answer:
                'Not necessarily. We can provide retainer support for websites built by other developers. However, we work most efficiently with sites we built because we know the codebase.',
            },
            {
              question: 'How do email campaigns work?',
              answer:
                'We write, design, and schedule email campaigns aligned with your festival calendar. You review and approve before sending. We track open rates, click rates, and adjust strategy based on data.',
            },
            {
              question: 'Can we request additional features or projects during the retainer?',
              answer:
                'Yes. Small features are included in your monthly hours. Larger projects (new application forms, major redesigns) can be quoted separately.',
            },
            {
              question: 'How is this different from hiring a full-time marketing person?',
              answer:
                "You get specialized festival website + digital marketing expertise for a fraction of the cost of a full-time hire. Plus, we bring technical skills (development, integrations, analytics) that most marketing staff don't have.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Grow Your Festival&apos;s Digital Presence?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your festival&apos;s needs and find the right retainer
            tier for your goals.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Schedule Your Strategy Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just an honest conversation about how ongoing support can help your festival grow.
          </p>
        </div>
      </section>
    </div>
  )
}
